import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSucursal } from '@/context/SucursalContext';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import * as mantenimientosApiService from '@/services/mantenimientosApiService';
import * as citasApiService from '@/services/citasApiService';
import * as serviciosApiService from '@/services/serviciosApiService';
import * as repuestosApiService from '@/services/repuestosApiService';
import * as vehiculosApiService from '@/services/vehiculosApiService';
import * as clientesApiService from '@/services/clientesApiService';
import * as mecanicosApiService from '@/services/mecanicosApiService';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import moment from 'moment';
import 'moment/locale/es';
import { FaCalendarAlt, FaUsers, FaCar, FaTools, FaWarehouse, FaExclamationTriangle, FaSync, FaExclamationCircle } from 'react-icons/fa';
import TrialExpiredModal from '@/components/common/TrialExpiredModal';
import Select from '@/components/common/Select';
import Loader from '@/components/common/Loader';


// Estilos CSS para alinear las leyendas
const legendStyles = `
  .recharts-legend-wrapper {
    padding-top: 12px !important;
    line-height: 1.5 !important;
  }
  
  .recharts-legend-item {
    display: inline-flex !important;
    align-items: center !important;
    margin-right: 20px !important;
    margin-bottom: 8px !important;
    vertical-align: middle !important;
  }
  
  .recharts-legend-item .recharts-legend-item-text {
    margin-left: 6px !important;
    line-height: 1.2 !important;
    vertical-align: middle !important;
    display: inline-block !important;
  }
  
  .recharts-legend-item svg {
    vertical-align: middle !important;
    margin-top: 0 !important;
  }
  
  .recharts-legend-item-circle {
    width: 14px !important;
    height: 14px !important;
  }
`;

// Inyectar estilos en el head solo una vez
if (typeof document !== 'undefined' && !document.querySelector('#recharts-legend-styles')) {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.id = "recharts-legend-styles";
  styleSheet.innerText = legendStyles;
  document.head.appendChild(styleSheet);
}

// Configurar moment para usar español
moment.locale('es');

// Componente de leyenda personalizado
const CustomLegend = ({ payload }) => {
  if (!payload || !payload.length) return null;
  
  return (
    <div className="flex flex-wrap items-center justify-center gap-4 mt-3">
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-sm text-gray-200">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

// Componente de leyenda externa para colocar antes de los gráficos
const ExternalLegend = ({ data, title }) => {
  if (!data || !data.length) return null;
  
  return (
    <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-750 rounded-lg">
      <span className="text-xs text-gray-400 font-medium mr-2">{title}:</span>
      {data.map((item, index) => (
        <div key={index} className="flex items-center space-x-1">
          <div 
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: item.color }}
          />
          <span className="text-xs text-gray-300">{item.name}</span>
        </div>
      ))}
    </div>
  );
};

// Colores para gráficos
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];
const STATUS_COLORS = {
  1: '#FFBB28', // Pendiente
  2: '#0088FE', // En Progreso  
  3: '#FF8042', // Cancelada
  'Completada': '#00C49F'
};

// Mapeo de estado_id a nombres
const ESTADO_NOMBRES = {
  1: 'Pendiente',
  2: 'En Progreso',
  3: 'Cancelada'
};

/**
 * Dashboard principal para administradores
 * Muestra estadísticas y gráficos sobre citas, clientes, vehículos, etc.
 */
const Dashboard = () => {
  // Estados para datos
  const [citas, setCitas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [repuestos, setRepuestos] = useState([]);
  const [mantenimientos, setMantenimientos] = useState([]);
  const [mecanicos, setMecanicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const dataCacheRef = useRef(new Map());
  const lastLoadRef = useRef({ empresaId: null, sucursalId: null });
  
  // Estados para filtros
  const [periodoTiempo, setPeriodoTiempo] = useState('semana');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [stockBajo, setStockBajo] = useState([]);
  

  
  // Obtener sucursal seleccionada y empresa actual
  const { selectedSucursal, sucursalData, clearSucursalesCache } = useSucursal();
  const { empresaActual, isSuperAdmin } = useSimpleEmpresa();
  
  // Función optimizada para cargar datos con caché
  const loadData = useCallback(async () => {
      const empresaId = empresaActual?.id;
      const sucursalId = sucursalData?.id;
      
      /*console.log('🔄 [Dashboard] loadData ejecutado', {
        empresaId,
        sucursalId,
        isSuperAdmin,
        lastLoad: lastLoadRef.current
      });*/
      
      // Verificar si realmente cambió algo para evitar llamadas innecesarias
      if (lastLoadRef.current.empresaId === empresaId && 
          lastLoadRef.current.sucursalId === sucursalId) {
        //console.log('⏭️ [Dashboard] Mismos parámetros, no recargando');
        return;
      }
      
      // Para SuperAdmin, puede continuar sin sucursal seleccionada
      // Para usuarios regulares, necesita sucursal
      if (!isSuperAdmin && !sucursalId) {
        //console.log('⏹️ [Dashboard] No hay sucursal seleccionada, esperando...');
        return;
      }

      // Para SuperAdmin, necesita empresa seleccionada
      if (isSuperAdmin && !empresaId) {
        //console.log('⏹️ [Dashboard] SuperAdmin sin empresa seleccionada, esperando...');
        return;
      }
      
      // Actualizar última carga
      lastLoadRef.current = { empresaId, sucursalId };

      setLoading(true);
      try {
        
        /*console.log('🔄 Cargando datos del dashboard:', {
          empresaId,
          sucursalId,
          isSuperAdmin,
          empresaName: empresaActual?.nombre,
          sucursalName: sucursalData?.Nombre
        });*/
        
        const cacheKey = `${empresaId}-${sucursalId}`;
        
        // Verificar caché
        if (dataCacheRef.current.has(cacheKey)) {
          const cachedData = dataCacheRef.current.get(cacheKey);
          const cacheAge = Date.now() - cachedData.timestamp;
          
          // Usar caché si es menor a 2 minutos
          if (cacheAge < 120000) {
            //console.log('📦 Usando datos desde caché');
            setCitas(cachedData.citas || []);
            setClientes(cachedData.clientes || []);
            setVehiculos(cachedData.vehiculos || []);
            setServicios(cachedData.servicios || []);
            setMantenimientos(cachedData.mantenimientos || []);
            setMecanicos(cachedData.mecanicos || []);
            setStockBajo(cachedData.stockBajo || []);
            setLoading(false);
            return;
          }
        }

        // Cargar datos básicos y vehículos en paralelo
        const [citasData, clientesData, serviciosData, mantenimientosData, mecanicosData, vehiculosData, repuestosBajoStock] = await Promise.all([
          sucursalId ? citasApiService.getCitas(sucursalId) : Promise.resolve([]),
          clientesApiService.getClientes(),
          sucursalId ? serviciosApiService.getServicios(sucursalId) : Promise.resolve([]),
          sucursalId ? mantenimientosApiService.getMantenimientos({ sucursal_id: sucursalId }) : Promise.resolve([]),
          sucursalId ? mecanicosApiService.getMecanicos(sucursalId) : Promise.resolve([]),
          empresaId ? vehiculosApiService.getVehiculos(empresaId).catch((error) => {
            console.error('Error al cargar vehículos:', error);
            return [];
          }) : Promise.resolve([]),
          sucursalId ? repuestosApiService.getRepuestosStockBajo(sucursalId) : Promise.resolve([])
        ]);
        
        //console.log(`✅ Datos cargados: ${citasData.length} citas, ${clientesData.length} clientes, ${vehiculosData.length} vehículos, ${mantenimientosData.length} mantenimientos, ${mecanicosData.length} mecánicos`);
        
        // Guardar en caché
        const newCacheData = {
          citas: citasData || [],
          clientes: clientesData || [],
          vehiculos: vehiculosData || [],
          servicios: serviciosData || [],
          mantenimientos: mantenimientosData || [],
          mecanicos: mecanicosData || [],
          stockBajo: repuestosBajoStock || [],
          timestamp: Date.now()
        };
        
        dataCacheRef.current.set(cacheKey, newCacheData);
        
        setCitas(newCacheData.citas);
        setClientes(newCacheData.clientes);
        setVehiculos(newCacheData.vehiculos);
        setServicios(newCacheData.servicios);
        setMantenimientos(newCacheData.mantenimientos);
        setMecanicos(newCacheData.mecanicos);
        setStockBajo(newCacheData.stockBajo);
        
        setLoading(false);
      } catch (error) {
        console.error("Error al cargar datos del dashboard:", error);
        setLoading(false);
      }
  }, [sucursalData, empresaActual, isSuperAdmin]);

  // Recargar datos cuando cambia la sucursal seleccionada
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Función para forzar recarga de datos (limpiar caché)
  const forceRefresh = useCallback(() => {
    const empresaId = empresaActual?.id;
    const sucursalId = sucursalData?.id;
    const cacheKey = `${empresaId}-${sucursalId}`;
    
    // Limpiar caché del dashboard
    dataCacheRef.current.delete(cacheKey);
    
    // Resetear última carga para forzar recarga
    lastLoadRef.current = { empresaId: null, sucursalId: null };
    
    // Limpiar caché de sucursales también
    clearSucursalesCache();
    
    loadData();
  }, [empresaActual, sucursalData, loadData]);
  
  // Efecto para generar datos de ejemplo si no hay datos reales
  useEffect(() => {
    if (!loading && citas.length === 0) {
      //console.log('No hay citas, generando datos de ejemplo para visualización');
      
      // Generar algunas citas de ejemplo para mostrar en los gráficos
      const citasEjemplo = [];
      const hoy = moment().startOf('day');
      
      // Generar 10 citas de ejemplo distribuidas en los últimos 30 días
      for (let i = 0; i < 10; i++) {
        const diasAtras = Math.floor(Math.random() * 30);
        const estados = ['Pendiente', 'En Progreso', 'Completada', 'Cancelada'];
        const estadoAleatorio = estados[Math.floor(Math.random() * estados.length)];
        
        citasEjemplo.push({
          id: `ejemplo-${i}`,
          Fecha: hoy.clone().subtract(diasAtras, 'days').format('YYYY-MM-DD'),
          Estado: estadoAleatorio,
          Servicios: ['ejemplo-servicio-1', 'ejemplo-servicio-2'],
          Cliente: 'ejemplo-cliente',
          Patente: 'ejemplo-vehiculo'
        });
      }
      
      if (citasEjemplo.length > 0) {
        setCitas(citasEjemplo);
      }
    }
  }, [loading, citas]);
  
  // Filtrar citas por periodo de tiempo (memoizado)
  const citasFiltradas = useMemo(() => {
    const hoy = moment().startOf('day');
    let fechaInicioFiltro, fechaFinFiltro;
    
    if (periodoTiempo === 'otro') {
      // Usar fechas personalizadas
      if (!fechaInicio || !fechaFin) {
        return []; // Si no hay fechas, no mostrar datos
      }
      fechaInicioFiltro = moment(fechaInicio).startOf('day');
      fechaFinFiltro = moment(fechaFin).endOf('day');
    } else {
      // Usar períodos predefinidos
      switch (periodoTiempo) {
        case 'dia':
          fechaInicioFiltro = hoy.clone();
          break;
        case 'semana':
          fechaInicioFiltro = hoy.clone().subtract(6, 'days');
          break;
        case 'mes':
          fechaInicioFiltro = hoy.clone().subtract(29, 'days');
          break;
        case 'anio':
          fechaInicioFiltro = hoy.clone().startOf('year');
          break;
        default:
          fechaInicioFiltro = hoy.clone().subtract(6, 'days');
      }
      fechaFinFiltro = hoy.clone().endOf('day');
    }
    
    // Asegurarse de que citas es un array antes de filtrar
    if (!Array.isArray(citas)) {
      console.warn('citas no es un array:', citas);
      return [];
    }
    
    return citas.filter(cita => {
      // Asegurarse de que cita.Fecha existe y es válida
      if (!cita.Fecha) {
        return false;
      }
      
      const fechaCita = moment(cita.Fecha);
      return fechaCita.isValid() && 
             fechaCita.isSameOrAfter(fechaInicioFiltro) && 
             fechaCita.isSameOrBefore(fechaFinFiltro);
    });
  }, [citas, periodoTiempo, fechaInicio, fechaFin]);
  
  
  // Preparar datos para gráfico de citas por estado (memoizado)
  const datosEstadoCitas = useMemo(() => {
    const conteo = {
      1: 0, // Pendiente
      2: 0, // En Progreso
      3: 0, // Cancelada
      'Completada': 0
    };
    
    // Asegurarse de que citasFiltradas es un array
    if (Array.isArray(citasFiltradas)) {
      citasFiltradas.forEach(cita => {
        if (cita.finalizada === true) {
          // Si está finalizada, contar como completada
          conteo['Completada']++;
        } else if (cita.estado_id && conteo[cita.estado_id] !== undefined) {
          // Si no está finalizada, usar el estado_id
          conteo[cita.estado_id]++;
        }
      });
    }
    
    // Convertir a array para el gráfico
    const resultado = [];
    
    // Agregar estados por ID
    Object.keys(ESTADO_NOMBRES).forEach(estadoId => {
      const id = parseInt(estadoId);
      resultado.push({
        name: ESTADO_NOMBRES[id],
        value: conteo[id],
        color: STATUS_COLORS[id]
      });
    });
    
    // Agregar completadas
    resultado.push({
      name: 'Completada',
      value: conteo['Completada'],
      color: STATUS_COLORS['Completada']
    });
    
    return resultado;
  }, [citasFiltradas]);
  
  // Preparar datos para gráfico de citas por día (memoizado)
  const datosCitasPorDia = useMemo(() => {
    const hoy = moment().startOf('day');
    const dias = [];
    const datosPorDia = {};
    
    // Crear array de días según el periodo seleccionado
    if (periodoTiempo === 'otro') {
      // Para período personalizado, generar días entre las fechas seleccionadas
      if (!fechaInicio || !fechaFin) {
        return []; // Si no hay fechas, no mostrar datos
      }
      
      const inicio = moment(fechaInicio);
      const fin = moment(fechaFin);
      const totalDias = fin.diff(inicio, 'days') + 1;
      
      // Si el rango es muy grande (más de 31 días), agrupar por mes
      const agruparPorMes = totalDias > 31;
      
      if (agruparPorMes) {
        let fechaActual = inicio.clone().startOf('month');
        const fechaFinMes = fin.clone().endOf('month');
        
        while (fechaActual.isSameOrBefore(fechaFinMes)) {
          const fechaStr = fechaActual.format('YYYY-MM');
          const formatoFecha = fechaActual.format('MMM YYYY');
          
          dias.push(fechaStr);
          datosPorDia[fechaStr] = {
            fecha: formatoFecha,
            total: 0,
            completadas: 0,
            pendientes: 0,
            enProgreso: 0
          };
          
          fechaActual.add(1, 'month');
        }
      } else {
        // Agrupar por día
        for (let i = 0; i < totalDias; i++) {
          const fecha = inicio.clone().add(i, 'days');
          const fechaStr = fecha.format('YYYY-MM-DD');
          const formatoFecha = fecha.format('DD/MM');
          
          dias.push(fechaStr);
          datosPorDia[fechaStr] = {
            fecha: formatoFecha,
            total: 0,
            completadas: 0,
            pendientes: 0,
            enProgreso: 0
          };
        }
      }
    } else if (periodoTiempo === 'anio') {
      // Para año, generar meses desde enero hasta el mes actual
      const inicioAno = moment().startOf('year');
      const mesActual = moment().month(); // 0-11
      
      for (let i = 0; i <= mesActual; i++) {
        const fecha = inicioAno.clone().add(i, 'months');
        const fechaStr = fecha.format('YYYY-MM');
        const formatoFecha = fecha.format('MMM');
        
        dias.push(fechaStr);
        datosPorDia[fechaStr] = {
          fecha: formatoFecha,
          total: 0,
          completadas: 0,
          pendientes: 0,
          enProgreso: 0
        };
      }
    } else {
      // Para períodos predefinidos (día, semana, mes)
      let numDias = 7;
      if (periodoTiempo === 'dia') numDias = 1;
      if (periodoTiempo === 'mes') numDias = 30;
      
      for (let i = numDias - 1; i >= 0; i--) {
        let fecha;
        let fechaStr;
        let formatoFecha;
        // Para día, semana y mes, agrupamos por día
        fecha = hoy.clone().subtract(i, 'days');
        fechaStr = fecha.format('YYYY-MM-DD');
        formatoFecha = fecha.format('DD/MM');
        
        dias.push(fechaStr);
        datosPorDia[fechaStr] = {
          fecha: formatoFecha,
          total: 0,
          completadas: 0,
          pendientes: 0,
          enProgreso: 0
        };
      }
    }
    
    // Asegurarse de que citasFiltradas es un array
    if (Array.isArray(citasFiltradas)) {
      // Contar citas por día o mes según el periodo
      citasFiltradas.forEach(cita => {
        if (!cita.Fecha) return;
        
        const fechaCitaMoment = moment(cita.Fecha);
        let fechaClave;
        
        if (periodoTiempo === 'anio') {
          // Para año, agrupamos por mes
          fechaClave = fechaCitaMoment.format('YYYY-MM');
        } else if (periodoTiempo === 'otro') {
          // Para período personalizado, determinar si agrupar por mes o día
          const inicio = moment(fechaInicio);
          const fin = moment(fechaFin);
          const totalDias = fin.diff(inicio, 'days') + 1;
          
          if (totalDias > 31) {
            fechaClave = fechaCitaMoment.format('YYYY-MM');
          } else {
            fechaClave = fechaCitaMoment.format('YYYY-MM-DD');
          }
        } else {
          // Para día, semana y mes, agrupamos por día
          fechaClave = fechaCitaMoment.format('YYYY-MM-DD');
        }
        
        if (datosPorDia[fechaClave]) {
          datosPorDia[fechaClave].total++;
          
          if (cita.finalizada === true) {
            datosPorDia[fechaClave].completadas++;
          } else if (cita.estado_id === 1) {
            datosPorDia[fechaClave].pendientes++;
          } else if (cita.estado_id === 2) {
            datosPorDia[fechaClave].enProgreso++;
          }
        }
      });
    }
    
    // Convertir a array para el gráfico
    return dias.map(dia => datosPorDia[dia]);
  }, [citasFiltradas, periodoTiempo, fechaInicio, fechaFin]);
  
  // Preparar datos para gráfico de servicios más solicitados basado en mantenimientos (memoizado)
  const datosServiciosSolicitados = useMemo(() => {
    const conteoServicios = {};
    
    // Usar datos de mantenimientos reales
    if (Array.isArray(mantenimientos)) {
      mantenimientos.forEach(mantenimiento => {
        if (mantenimiento.mantenimiento_servicios && Array.isArray(mantenimiento.mantenimiento_servicios)) {
          mantenimiento.mantenimiento_servicios.forEach(servicioItem => {
            const servicio = servicioItem.servicio || servicioItem.servicios;
            if (servicio && servicio.nombre) {
              const nombreServicio = servicio.nombre;
              if (!conteoServicios[nombreServicio]) {
                conteoServicios[nombreServicio] = 0;
              }
              conteoServicios[nombreServicio]++;
            }
          });
        }
      });
    }
    
    // Convertir a array y ordenar
    const resultado = Object.keys(conteoServicios)
      .map(nombre => ({
        nombre,
        cantidad: conteoServicios[nombre]
      }))
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 5); // Top 5 servicios
    
    // Si no hay resultados, mostrar mensaje
    if (resultado.length === 0) {
      return [
        { nombre: 'Sin datos', cantidad: 0 }
      ];
    }
    
    return resultado;
  }, [mantenimientos]);

  // Preparar datos para gráfico de ingresos por mantenimientos
  const ingresosPorPeriodo = () => {
    const hoy = moment().startOf('day');
    const ingresos = {};
    
    // Crear estructura de períodos
    if (periodoTiempo === 'otro') {
      // Para período personalizado
      if (!fechaInicio || !fechaFin) {
        return []; // Si no hay fechas, no mostrar datos
      }
      
      const inicio = moment(fechaInicio);
      const fin = moment(fechaFin);
      const totalDias = fin.diff(inicio, 'days') + 1;
      
      // Si el rango es muy grande (más de 31 días), agrupar por mes
      const agruparPorMes = totalDias > 31;
      
      if (agruparPorMes) {
        let fechaActual = inicio.clone().startOf('month');
        const fechaFinMes = fin.clone().endOf('month');
        
        while (fechaActual.isSameOrBefore(fechaFinMes)) {
          const clave = fechaActual.format('YYYY-MM');
          const formatoFecha = fechaActual.format('MMM YYYY');
          
          ingresos[clave] = {
            fecha: formatoFecha,
            ingresos: 0,
            cantidad: 0
          };
          
          fechaActual.add(1, 'month');
        }
      } else {
        // Agrupar por día
        for (let i = 0; i < totalDias; i++) {
          const fecha = inicio.clone().add(i, 'days');
          const clave = fecha.format('YYYY-MM-DD');
          const formatoFecha = fecha.format('DD/MM');
          
          ingresos[clave] = {
            fecha: formatoFecha,
            ingresos: 0,
            cantidad: 0
          };
        }
      }
    } else if (periodoTiempo === 'anio') {
      // Para año, generar meses desde enero hasta el mes actual
      const inicioAno = moment().startOf('year');
      const mesActual = moment().month(); // 0-11
      
      for (let i = 0; i <= mesActual; i++) {
        const fecha = inicioAno.clone().add(i, 'months');
        const clave = fecha.format('YYYY-MM');
        const formatoFecha = fecha.format('MMM');
        
        ingresos[clave] = {
          fecha: formatoFecha,
          ingresos: 0,
          cantidad: 0
        };
      }
    } else {
      let numPeriodos = 7;
      let unidadTiempo = 'days';
      let formatoFecha = 'DD/MM';
      
      if (periodoTiempo === 'dia') {
        numPeriodos = 24;
        unidadTiempo = 'hours';
        formatoFecha = 'HH:mm';
      } else if (periodoTiempo === 'mes') {
        numPeriodos = 30;
        unidadTiempo = 'days';
        formatoFecha = 'DD/MM';
      }
      
      // Inicializar períodos
      for (let i = numPeriodos - 1; i >= 0; i--) {
        const fecha = hoy.clone().subtract(i, unidadTiempo);
        const clave = periodoTiempo === 'dia' 
          ? fecha.format('YYYY-MM-DD-HH')
          : fecha.format('YYYY-MM-DD');
        
        ingresos[clave] = {
          fecha: fecha.format(formatoFecha),
          ingresos: 0,
          cantidad: 0
        };
      }
    }
    
    // Sumar ingresos de mantenimientos filtrados
    const mantenimientosFiltrados = filtrarMantenimientosPorPeriodo();
    if (Array.isArray(mantenimientosFiltrados)) {
      mantenimientosFiltrados.forEach(mantenimiento => {
        const fechaMantenimiento = moment(mantenimiento.fecha);
        let clave;
        
        if (periodoTiempo === 'dia') {
          clave = fechaMantenimiento.format('YYYY-MM-DD-HH');
        } else if (periodoTiempo === 'anio') {
          clave = fechaMantenimiento.format('YYYY-MM');
        } else if (periodoTiempo === 'otro') {
          // Para período personalizado, determinar si agrupar por mes o día
          const inicio = moment(fechaInicio);
          const fin = moment(fechaFin);
          const totalDias = fin.diff(inicio, 'days') + 1;
          
          if (totalDias > 31) {
            clave = fechaMantenimiento.format('YYYY-MM');
          } else {
            clave = fechaMantenimiento.format('YYYY-MM-DD');
          }
        } else {
          clave = fechaMantenimiento.format('YYYY-MM-DD');
        }
        
        if (ingresos[clave]) {
          ingresos[clave].ingresos += parseFloat(mantenimiento.total || 0);
          ingresos[clave].cantidad++;
        }
      });
    }
    
    return Object.values(ingresos);
  };

  // Filtrar mantenimientos por periodo de tiempo
  const filtrarMantenimientosPorPeriodo = () => {
    const hoy = moment().startOf('day');
    let fechaInicioFiltro, fechaFinFiltro;
    
    if (periodoTiempo === 'otro') {
      // Usar fechas personalizadas
      if (!fechaInicio || !fechaFin) {
        return []; // Si no hay fechas, no mostrar datos
      }
      fechaInicioFiltro = moment(fechaInicio).startOf('day');
      fechaFinFiltro = moment(fechaFin).endOf('day');
    } else {
      // Usar períodos predefinidos
      switch (periodoTiempo) {
        case 'dia':
          fechaInicioFiltro = hoy.clone();
          break;
        case 'semana':
          fechaInicioFiltro = hoy.clone().subtract(6, 'days');
          break;
        case 'mes':
          fechaInicioFiltro = hoy.clone().subtract(29, 'days');
          break;
        case 'anio':
          fechaInicioFiltro = hoy.clone().startOf('year');
          break;
        default:
          fechaInicioFiltro = hoy.clone().subtract(6, 'days');
      }
      fechaFinFiltro = hoy.clone().endOf('day');
    }
    
    if (!Array.isArray(mantenimientos)) {
      return [];
    }
    
    return mantenimientos.filter(mantenimiento => {
      if (!mantenimiento.fecha) {
        return false;
      }
      
      const fechaMantenimiento = moment(mantenimiento.fecha);
      return fechaMantenimiento.isValid() && 
             fechaMantenimiento.isSameOrAfter(fechaInicioFiltro) && 
             fechaMantenimiento.isSameOrBefore(fechaFinFiltro);
    });
  };

  // Preparar datos para gráfico de mantenimientos cerrados por mecánico
  const mantenimientosPorMecanico = () => {
    const mantenimientosFiltrados = filtrarMantenimientosPorPeriodo().filter(m => m.finalizada === true);
    // Inicializar conteo para todos los mecánicos con servicios
    const datosMecanicos = {};
    const tiposServicios = new Set();
    
    mecanicos.forEach(mecanico => {
      datosMecanicos[mecanico.id] = {
        nombre: mecanico.Nombre || mecanico.nombre,
        mecanicoId: mecanico.id,
        servicios: {},
        total: 0
      };
    });
    
    // Contar servicios por mecánico en mantenimientos finalizados
    mantenimientosFiltrados.forEach(mantenimiento => {
      if (mantenimiento.finalizada === true) {
        // Determinar mecánicos responsables
        let mecanicosResponsables = [];
        
        if (mantenimiento.mecanico_id) {
          mecanicosResponsables = [mantenimiento.mecanico_id];
        } else if (mantenimiento.mantenimiento_mecanicos && Array.isArray(mantenimiento.mantenimiento_mecanicos)) {
          mecanicosResponsables = mantenimiento.mantenimiento_mecanicos.map(rel => rel.mecanico_id);
        }
        
        // Contar servicios para cada mecánico
        mecanicosResponsables.forEach(mecanicoId => {
          if (datosMecanicos[mecanicoId] && mantenimiento.mantenimiento_servicios) {
            mantenimiento.mantenimiento_servicios.forEach(servicioItem => {
              const servicio = servicioItem.servicios || servicioItem.servicio;
              if (servicio && servicio.nombre) {
                const nombreServicio = servicio.nombre;
                tiposServicios.add(nombreServicio);
                
                if (!datosMecanicos[mecanicoId].servicios[nombreServicio]) {
                  datosMecanicos[mecanicoId].servicios[nombreServicio] = 0;
                }
                datosMecanicos[mecanicoId].servicios[nombreServicio]++;
                datosMecanicos[mecanicoId].total++;
              }
            });
          }
        });
      }
    });
    
    // Convertir a array y ordenar por total
    const tiposServiciosArray = Array.from(tiposServicios).slice(0, 6); // Top 6 servicios
    
    const resultado = mecanicos.map(mecanico => ({
      nombre: mecanico.Nombre || mecanico.nombre,
      mecanicoId: mecanico.id,
      total: datosMecanicos[mecanico.id].total,
      servicios: datosMecanicos[mecanico.id].servicios,
      tiposServicios: tiposServiciosArray
    }))
    .sort((a, b) => b.total - a.total)
    .filter(mecanico => mecanico.total > 0); // Solo mecánicos con servicios
    
    if (resultado.length === 0) {
      return { mecánicos: [], servicios: [] };
    }
    
    return { mecánicos: resultado, servicios: tiposServiciosArray };
  };

  // Preparar datos detallados para gráfico de ingresos por período
  const ingresosPorPeriodoDetallado = () => {
    const hoy = moment().startOf('day');
    const ingresos = {};
    
    // Crear estructura de períodos (similar a ingresosPorPeriodo)
    if (periodoTiempo === 'otro') {
      if (!fechaInicio || !fechaFin) {
        return [];
      }
      
      const inicio = moment(fechaInicio);
      const fin = moment(fechaFin);
      const totalDias = fin.diff(inicio, 'days') + 1;
      const agruparPorMes = totalDias > 31;
      
      if (agruparPorMes) {
        let fechaActual = inicio.clone().startOf('month');
        const fechaFinMes = fin.clone().endOf('month');
        
        while (fechaActual.isSameOrBefore(fechaFinMes)) {
          const clave = fechaActual.format('YYYY-MM');
          const formatoFecha = fechaActual.format('MMM YYYY');
          
          ingresos[clave] = {
            fecha: formatoFecha,
            servicios: 0,
            repuestos: 0,
            iva: 0,
            adicionales: 0,
            // Almacenar valores originales para tooltip
            serviciosOriginal: 0,
            repuestosOriginal: 0,
            ivaOriginal: 0,
            adicionalesOriginal: 0
          };
          
          fechaActual.add(1, 'month');
        }
      } else {
        for (let i = 0; i < totalDias; i++) {
          const fecha = inicio.clone().add(i, 'days');
          const clave = fecha.format('YYYY-MM-DD');
          const formatoFecha = fecha.format('DD/MM');
          
          ingresos[clave] = {
            fecha: formatoFecha,
            servicios: 0,
            repuestos: 0,
            iva: 0,
            adicionales: 0,
            // Almacenar valores originales para tooltip
            serviciosOriginal: 0,
            repuestosOriginal: 0,
            ivaOriginal: 0,
            adicionalesOriginal: 0
          };
        }
      }
    } else if (periodoTiempo === 'anio') {
      const inicioAno = moment().startOf('year');
      const mesActual = moment().month();
      
      for (let i = 0; i <= mesActual; i++) {
        const fecha = inicioAno.clone().add(i, 'months');
        const clave = fecha.format('YYYY-MM');
        const formatoFecha = fecha.format('MMM');
        
        ingresos[clave] = {
          fecha: formatoFecha,
          servicios: 0,
          repuestos: 0,
          iva: 0,
          adicionales: 0,
          // Almacenar valores originales para tooltip
          serviciosOriginal: 0,
          repuestosOriginal: 0,
          ivaOriginal: 0,
          adicionalesOriginal: 0
        };
      }
    } else {
      let numPeriodos = 7;
      let unidadTiempo = 'days';
      let formatoFecha = 'DD/MM';
      
      if (periodoTiempo === 'dia') {
        numPeriodos = 24;
        unidadTiempo = 'hours';
        formatoFecha = 'HH:mm';
      } else if (periodoTiempo === 'mes') {
        numPeriodos = 30;
        unidadTiempo = 'days';
        formatoFecha = 'DD/MM';
      }
      
      for (let i = numPeriodos - 1; i >= 0; i--) {
        const fecha = hoy.clone().subtract(i, unidadTiempo);
        const clave = periodoTiempo === 'dia' 
          ? fecha.format('YYYY-MM-DD-HH')
          : fecha.format('YYYY-MM-DD');
        
        ingresos[clave] = {
          fecha: fecha.format(formatoFecha),
          servicios: 0,
          repuestos: 0,
          iva: 0,
          adicionales: 0,
          // Almacenar valores originales para tooltip
          serviciosOriginal: 0,
          repuestosOriginal: 0,
          ivaOriginal: 0,
          adicionalesOriginal: 0
        };
      }
    }
    
    // Sumar ingresos detallados de mantenimientos filtrados
    const mantenimientosFiltrados = filtrarMantenimientosPorPeriodo();
    if (Array.isArray(mantenimientosFiltrados)) {
      mantenimientosFiltrados.forEach(mantenimiento => {
        const fechaMantenimiento = moment(mantenimiento.fecha);
        let clave;
        
        if (periodoTiempo === 'dia') {
          clave = fechaMantenimiento.format('YYYY-MM-DD-HH');
        } else if (periodoTiempo === 'anio') {
          clave = fechaMantenimiento.format('YYYY-MM');
        } else if (periodoTiempo === 'otro') {
          const inicio = moment(fechaInicio);
          const fin = moment(fechaFin);
          const totalDias = fin.diff(inicio, 'days') + 1;
          
          if (totalDias > 31) {
            clave = fechaMantenimiento.format('YYYY-MM');
          } else {
            clave = fechaMantenimiento.format('YYYY-MM-DD');
          }
        } else {
          clave = fechaMantenimiento.format('YYYY-MM-DD');
        }
        
        if (ingresos[clave]) {
          // Valores originales para tooltip (pueden ser negativos)
          const serviciosVal = parseFloat(mantenimiento.total_servicios || 0);
          const repuestosVal = parseFloat(mantenimiento.total_repuestos || 0);
          const ivaVal = parseFloat(mantenimiento.monto_iva || 0);
          const adicionalesVal = parseFloat(mantenimiento.total_items_adicionales || 0);
          
          ingresos[clave].serviciosOriginal += serviciosVal;
          ingresos[clave].repuestosOriginal += repuestosVal;
          ingresos[clave].ivaOriginal += ivaVal;
          ingresos[clave].adicionalesOriginal += adicionalesVal;
          
          // Valores para la barra (siempre positivos)
          ingresos[clave].servicios += Math.abs(serviciosVal);
          ingresos[clave].repuestos += Math.abs(repuestosVal);
          ingresos[clave].iva += Math.abs(ivaVal);
          ingresos[clave].adicionales += Math.abs(adicionalesVal);
        }
      });
    }
    
    return Object.values(ingresos);
  };

  // Calcular totales para la tabla de resumen
  const calcularTotalesPorCategoria = () => {
    const datos = ingresosPorPeriodoDetallado();
    const totales = {
      servicios: 0,
      repuestos: 0,
      iva: 0,
      adicionales: 0
    };
    
    datos.forEach(item => {
      totales.servicios += item.serviciosOriginal || 0;
      totales.repuestos += item.repuestosOriginal || 0;
      totales.iva += item.ivaOriginal || 0;
      totales.adicionales += item.adicionalesOriginal || 0;
    });
    
    totales.total = totales.servicios + totales.repuestos + totales.iva + totales.adicionales;
    
    return totales;
  };

  // Preparar datos para gráfico de repuestos más usados
  const repuestosMasUsados = () => {
    const mantenimientosFiltrados = filtrarMantenimientosPorPeriodo().filter(m => m.finalizada === true);
    const conteoRepuestos = {};
    
    // Contar repuestos usados en mantenimientos finalizados
    mantenimientosFiltrados.forEach(mantenimiento => {
      if (mantenimiento.finalizada === true && mantenimiento.mantenimiento_repuestos) {
        mantenimiento.mantenimiento_repuestos.forEach(repuestoItem => {
          const repuesto = repuestoItem.repuestos || repuestoItem.repuesto;
          if (repuesto && repuesto.nombre) {
            const nombreRepuesto = repuesto.nombre;
            const cantidad = repuestoItem.cantidad || 1;
            
            if (!conteoRepuestos[nombreRepuesto]) {
              conteoRepuestos[nombreRepuesto] = 0;
            }
            conteoRepuestos[nombreRepuesto] += cantidad;
          }
        });
      }
    });
    
    // Convertir a array y tomar los top 8
    const resultado = Object.keys(conteoRepuestos)
      .map(nombre => ({
        name: nombre,
        value: conteoRepuestos[nombre]
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
    
    if (resultado.length === 0) {
      return [{ name: 'Sin datos', value: 0 }];
    }
    
    return resultado;
  };

  // Preparar datos para gráfico de ingresos por mecánico
  const ingresosPorMecanico = () => {
    const mantenimientosFiltrados = filtrarMantenimientosPorPeriodo();
    const ingresosMecanicos = {};
    
    // Inicializar todos los mecánicos con 0
    mecanicos.forEach(mecanico => {
      ingresosMecanicos[mecanico.id] = 0;
    });
    
    // Sumar ingresos de mantenimientos finalizados
    mantenimientosFiltrados.forEach(mantenimiento => {
      if (mantenimiento.finalizada === true && mantenimiento.total) {
        const ingreso = parseFloat(mantenimiento.total) || 0;
        
        // Opción 1: Si el mantenimiento tiene un mecanico_id directo
        if (mantenimiento.mecanico_id && ingresosMecanicos[mantenimiento.mecanico_id] !== undefined) {
          ingresosMecanicos[mantenimiento.mecanico_id] += ingreso;
        }
        // Opción 2: Si hay relación en mantenimiento_mecanicos
        else if (mantenimiento.mantenimiento_mecanicos && Array.isArray(mantenimiento.mantenimiento_mecanicos)) {
          const numMecanicos = mantenimiento.mantenimiento_mecanicos.length;
          const ingresoPorMecanico = ingreso / numMecanicos; // Dividir entre mecánicos
          
          mantenimiento.mantenimiento_mecanicos.forEach(rel => {
            const mecanicoId = rel.mecanico_id;
            if (mecanicoId && ingresosMecanicos[mecanicoId] !== undefined) {
              ingresosMecanicos[mecanicoId] += ingresoPorMecanico;
            }
          });
        }
      }
    });
    
    // Convertir a array
    const resultado = mecanicos.map(mecanico => ({
      nombre: mecanico.Nombre || mecanico.nombre,
      ingresos: Math.round(ingresosMecanicos[mecanico.id] || 0),
      mecanicoId: mecanico.id
    }))
    .sort((a, b) => b.ingresos - a.ingresos);
    
    if (resultado.length === 0) {
      return [{ nombre: 'Sin mecánicos', ingresos: 0 }];
    }
    
    return resultado;
  };

  // Preparar datos para gráfico de tipos de servicios por mecánico
  const tiposServiciosPorMecanico = () => {
    const mantenimientosFiltrados = filtrarMantenimientosPorPeriodo().filter(m => m.finalizada === true);
    const serviciosPorMecanico = {};
    const tiposServicios = new Set();
    
    // Inicializar todos los mecánicos
    mecanicos.forEach(mecanico => {
      serviciosPorMecanico[mecanico.id] = {
        nombre: mecanico.Nombre || mecanico.nombre,
        servicios: {}
      };
    });
    
    // Contar servicios por mecánico
    mantenimientosFiltrados.forEach(mantenimiento => {
      if (mantenimiento.finalizada === true && mantenimiento.mantenimiento_servicios) {
        // Determinar qué mecánico realizó este mantenimiento
        let mecanicosResponsables = [];
        
        if (mantenimiento.mecanico_id) {
          mecanicosResponsables = [mantenimiento.mecanico_id];
        } else if (mantenimiento.mantenimiento_mecanicos && Array.isArray(mantenimiento.mantenimiento_mecanicos)) {
          mecanicosResponsables = mantenimiento.mantenimiento_mecanicos.map(rel => rel.mecanico_id);
        }
        
        // Contar servicios para cada mecánico responsable
        mecanicosResponsables.forEach(mecanicoId => {
          if (serviciosPorMecanico[mecanicoId]) {
            mantenimiento.mantenimiento_servicios.forEach(servicioItem => {
              const servicio = servicioItem.servicios || servicioItem.servicio;
              if (servicio && servicio.nombre) {
                const nombreServicio = servicio.nombre;
                tiposServicios.add(nombreServicio);
                
                if (!serviciosPorMecanico[mecanicoId].servicios[nombreServicio]) {
                  serviciosPorMecanico[mecanicoId].servicios[nombreServicio] = 0;
                }
                serviciosPorMecanico[mecanicoId].servicios[nombreServicio]++;
              }
            });
          }
        });
      }
    });
    
    // Convertir a formato para gráfico de barras apiladas
    const tiposServiciosArray = Array.from(tiposServicios).slice(0, 6); // Top 6 tipos de servicios
    
    const resultado = mecanicos.map(mecanico => {
      const mecanicoData = {
        nombre: mecanico.Nombre || mecanico.nombre
      };
      
      // Añadir conteo para cada tipo de servicio
      tiposServiciosArray.forEach(tipoServicio => {
        mecanicoData[tipoServicio] = serviciosPorMecanico[mecanico.id]?.servicios[tipoServicio] || 0;
      });
      
      return mecanicoData;
    })
    .filter(mecanico => {
      // Solo incluir mecánicos que han realizado al menos un servicio
      return tiposServiciosArray.some(tipo => mecanico[tipo] > 0);
    });
    
    if (resultado.length === 0) {
      return { data: [{ nombre: 'Sin datos' }], servicios: [] };
    }
    
    return { data: resultado, servicios: tiposServiciosArray };
  };
  
  // Calcular datos de gráficos una sola vez (memoizados)
  const datosMantenimientosPorMecanico = useMemo(() => mantenimientosPorMecanico(), [mantenimientos, mecanicos, periodoTiempo, fechaInicio, fechaFin]);
  const datosTiposServiciosPorMecanico = useMemo(() => tiposServiciosPorMecanico(), [mantenimientos, mecanicos, periodoTiempo, fechaInicio, fechaFin]);
  const datosRepuestosMasUsados = useMemo(() => repuestosMasUsados(), [mantenimientos, periodoTiempo, fechaInicio, fechaFin]);
  const datosIngresosPorMecanico = useMemo(() => ingresosPorMecanico(), [mantenimientos, mecanicos, periodoTiempo, fechaInicio, fechaFin]);
  const datosIngresos = useMemo(() => ingresosPorPeriodoDetallado(), [mantenimientos, periodoTiempo, fechaInicio, fechaFin]);
  const totalesCategoria = useMemo(() => calcularTotalesPorCategoria(), [datosIngresos]);

  // Renderizar tarjeta de estadísticas
  const StatCard = ({ title, value, icon, color = 'blue' }) => (
    <div className={`bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-md`}>
      <div className="flex flex-col h-full">
        {/* Primera fila: Título */}
        <h3 className="text-sm font-medium text-gray-300 text-center mb-3">{title}</h3>
        
        {/* Segunda fila: Icono y número */}
        <div className="flex items-center justify-center space-x-2 flex-1">
          <div className={`bg-${color}-600 bg-opacity-20 p-2 rounded-full flex-shrink-0`}>
            {icon}
          </div>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );

  // Mostrar loading si la empresa está cargando
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-6">
        {/* Header con título */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4">
          <div className="mb-4 sm:mb-0">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold">Dashboard</h1>
              <div className="flex items-center gap-2">
                <button
                  onClick={forceRefresh}
                  disabled={loading}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
                  title="Actualizar datos"
                >
                  <FaSync className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                  Actualizar
                </button>
                <button
                  onClick={() => setShowTrialModal(true)}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
                  title="Ver estado de prueba"
                >
                  <FaExclamationCircle className="w-3 h-3" />
                  Estado
                </button>
              </div>
            </div>
            <p className="text-gray-400">
              {isSuperAdmin ? 
                `Resumen para ${empresaActual?.nombre || 'empresa'} ${sucursalData ? `- ${sucursalData.Nombre}` : '(todas las sucursales)'}` :
                `Resumen de actividad para ${sucursalData ? sucursalData.Nombre : 'todas las sucursales'}`
              }
            </p>
          </div>
          {/* Filtros de período */}
          <div className="flex flex-col sm:flex-row sm:justify-end sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-4">
            {/* Campos de fecha personalizada (lado izquierdo del filtro en desktop) */}
            {periodoTiempo === 'otro' && (
              <div className="w-full sm:w-auto sm:flex-shrink-0">
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-3 sm:px-3 sm:py-2">
                  {/* Móvil: 2 filas */}
                  <div className="block sm:hidden">
                    <div className="flex justify-between mb-2">
                      <label className="text-xs text-gray-400">Desde</label>
                      <label className="text-xs text-gray-400">Hasta</label>
                    </div>
                    <div className="flex space-x-3">
                      <input
                        type="date"
                        value={fechaInicio}
                        onChange={(e) => {
                          setFechaInicio(e.target.value);
                          // Si la fecha de fin es menor que la nueva fecha de inicio, actualizarla
                          if (fechaFin && moment(e.target.value).isAfter(moment(fechaFin))) {
                            setFechaFin(e.target.value);
                          }
                        }}
                        max={fechaFin || moment().format('YYYY-MM-DD')}
                        className="flex-1 bg-gray-700 text-white text-sm rounded px-3 py-2 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="date"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        min={fechaInicio}
                        max={moment().format('YYYY-MM-DD')}
                        className="flex-1 bg-gray-700 text-white text-sm rounded px-3 py-2 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  {/* Desktop: 1 fila */}
                  <div className="hidden sm:flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <label className="text-xs text-gray-400">Desde:</label>
                      <input
                        type="date"
                        value={fechaInicio}
                        onChange={(e) => {
                          setFechaInicio(e.target.value);
                          // Si la fecha de fin es menor que la nueva fecha de inicio, actualizarla
                          if (fechaFin && moment(e.target.value).isAfter(moment(fechaFin))) {
                            setFechaFin(e.target.value);
                          }
                        }}
                        max={fechaFin || moment().format('YYYY-MM-DD')}
                        className="bg-gray-700 text-white text-sm rounded px-2 py-1 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-xs text-gray-400">Hasta:</label>
                      <input
                        type="date"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        min={fechaInicio}
                        max={moment().format('YYYY-MM-DD')}
                        className="bg-gray-700 text-white text-sm rounded px-2 py-1 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Botones de periodo (siempre a la derecha) */}
            <div className="w-full sm:w-auto sm:flex-shrink-0">
              <div className="flex items-center justify-center bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <button 
                  className={`flex-1 sm:flex-initial px-3 py-2 text-xs sm:text-sm font-medium ${periodoTiempo === 'dia' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                  onClick={() => setPeriodoTiempo('dia')}
                >
                  Día
                </button>
                <button 
                  className={`flex-1 sm:flex-initial px-3 py-2 text-xs sm:text-sm font-medium ${periodoTiempo === 'semana' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                  onClick={() => setPeriodoTiempo('semana')}
                >
                  Semana
                </button>
                <button 
                  className={`flex-1 sm:flex-initial px-3 py-2 text-xs sm:text-sm font-medium ${periodoTiempo === 'mes' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                  onClick={() => setPeriodoTiempo('mes')}
                >
                  Mes
                </button>
                <button 
                  className={`flex-1 sm:flex-initial px-3 py-2 text-xs sm:text-sm font-medium ${periodoTiempo === 'anio' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                  onClick={() => setPeriodoTiempo('anio')}
                >
                  Año
                </button>
                <button 
                  className={`flex-1 sm:flex-initial px-3 py-2 text-xs sm:text-sm font-medium ${periodoTiempo === 'otro' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                  onClick={() => {
                    setPeriodoTiempo('otro');
                    // Establecer fechas por defecto (últimos 7 días)
                    if (!fechaInicio) {
                      setFechaInicio(moment().subtract(6, 'days').format('YYYY-MM-DD'));
                    }
                    if (!fechaFin) {
                      setFechaFin(moment().format('YYYY-MM-DD'));
                    }
                  }}
                >
                  Otro
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tarjetas de estadísticas */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard 
          title="Citas Totales" 
          value={citasFiltradas.length} 
          icon={<FaCalendarAlt className="text-blue-400" />} 
          color="blue"
        />
        <StatCard 
          title="Citas Completadas" 
          value={citasFiltradas.filter(c => c.finalizada === true).length} 
          icon={<FaCalendarAlt className="text-green-400" />} 
          color="green"
        />
        <StatCard 
          title="Mantenimientos" 
          value={mantenimientos.length} 
          icon={<FaTools className="text-purple-400" />} 
          color="purple"
        />
        <StatCard 
          title="Vehículos" 
          value={vehiculos.length} 
          icon={<FaCar className="text-yellow-400" />} 
          color="yellow"
        />
        <StatCard 
          title="Clientes Activos" 
          value={clientes.length} 
          icon={<FaUsers className="text-green-400" />} 
          color="green"
        />
        <StatCard 
          title="Stock Bajo" 
          value={stockBajo.length} 
          icon={<FaExclamationTriangle className="text-red-400" />} 
          color="red"
        />
      </div>
      
      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Gráfico de citas por día */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-md">
          <h2 className="text-lg font-medium mb-4">Citas por Día</h2>
          
          {/* Leyenda externa */}
          <ExternalLegend 
            data={[
              { name: 'Total', color: '#8884d8' },
              { name: 'Completadas', color: '#00C49F' },
              { name: 'Pendientes', color: '#FFBB28' },
              { name: 'En Progreso', color: '#0088FE' }
            ]}
            title="Estados"
          />
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={datosCitasPorDia}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis dataKey="fecha" stroke="#999" />
                <YAxis stroke="#999" allowDecimals={false} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #6b7280', 
                    borderRadius: '8px',
                    color: '#f9fafb'
                  }} 
                  labelStyle={{ color: '#f9fafb' }}
                  itemStyle={{ color: '#f9fafb' }}
                />
                <Line type="monotone" dataKey="total" name="Total" stroke="#8884d8" activeDot={{ r: 8 }} />
                <Line type="monotone" dataKey="completadas" name="Completadas" stroke="#00C49F" />
                <Line type="monotone" dataKey="pendientes" name="Pendientes" stroke="#FFBB28" />
                <Line type="monotone" dataKey="enProgreso" name="En Progreso" stroke="#0088FE" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Gráfico de citas por estado */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-md">
          <h2 className="text-lg font-medium mb-4">Citas por Estado</h2>
          
          {/* Leyenda externa */}
          <ExternalLegend 
            data={datosEstadoCitas}
            title="Estados"
          />
          
          <div className="h-80 flex justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={datosEstadoCitas}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {datosEstadoCitas.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #6b7280', 
                    borderRadius: '8px',
                    color: '#f9fafb'
                  }} 
                  labelStyle={{ color: '#f9fafb' }}
                  itemStyle={{ color: '#f9fafb' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Ingresos por Mantenimientos */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-md">
          <h2 className="text-lg font-medium mb-4">Ingresos por Mantenimientos</h2>
          
          {/* Leyenda externa */}
          <ExternalLegend 
            data={[
              { name: 'Servicios', color: '#0088FE' },
              { name: 'Repuestos', color: '#00C49F' },
              { name: 'IVA', color: '#FFBB28' },
              { name: 'Adicionales', color: '#FF8042' }
            ]}
            title="Categorías"
          />
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={datosIngresos}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis dataKey="fecha" stroke="#999" />
                <YAxis 
                  stroke="#999" 
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #6b7280', 
                    borderRadius: '8px',
                    color: '#f9fafb'
                  }} 
                  labelStyle={{ color: '#f9fafb' }}
                  itemStyle={{ color: '#f9fafb' }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-gray-800 border border-gray-600 rounded-lg p-3">
                          <p className="text-gray-200 font-medium mb-2">{label}</p>
                          <div className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-blue-300">Servicios:</span>
                              <span className="text-white font-medium">
                                ${data.serviciosOriginal?.toLocaleString() || '0'}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-green-300">Repuestos:</span>
                              <span className="text-white font-medium">
                                ${data.repuestosOriginal?.toLocaleString() || '0'}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-yellow-300">IVA:</span>
                              <span className="text-white font-medium">
                                ${data.ivaOriginal?.toLocaleString() || '0'}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-orange-300">Adicionales:</span>
                              <span className="text-white font-medium">
                                ${data.adicionalesOriginal?.toLocaleString() || '0'}
                              </span>
                            </div>
                            <div className="border-t border-gray-600 pt-2 mt-2">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-300 font-medium">Total:</span>
                                <span className="text-green-400 font-bold">
                                  ${((data.serviciosOriginal || 0) + (data.repuestosOriginal || 0) + (data.ivaOriginal || 0) + (data.adicionalesOriginal || 0)).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="servicios" stackId="a" name="Servicios" fill="#0088FE" />
                <Bar dataKey="repuestos" stackId="a" name="Repuestos" fill="#00C49F" />
                <Bar dataKey="iva" stackId="a" name="IVA" fill="#FFBB28" />
                <Bar dataKey="adicionales" stackId="a" name="Adicionales" fill="#FF8042" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Tabla de totales */}
          <div className="mt-4 bg-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-300 mb-3">Totales por Categoría</h3>
            {(() => {
              const totales = totalesCategoria;
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center justify-between p-2 bg-gray-800 rounded">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-xs text-gray-300">Servicios</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      ${totales.servicios.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-gray-800 rounded">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-xs text-gray-300">Repuestos</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      ${totales.repuestos.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-gray-800 rounded">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                      <span className="text-xs text-gray-300">IVA</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      ${totales.iva.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-gray-800 rounded">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                      <span className="text-xs text-gray-300">Adicionales</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      ${totales.adicionales.toLocaleString()}
                    </span>
                  </div>
                </div>
              );
            })()}
            
            {/* Total general */}
            <div className="mt-3 pt-3 border-t border-gray-600">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-300">Total General:</span>
                <span className="text-lg font-bold text-green-400">
                  ${totalesCategoria.total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Servicios más solicitados */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-md">
          <h2 className="text-lg font-medium mb-4">Servicios Más Realizados</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={datosServiciosSolicitados}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis type="number" stroke="#999" />
                <YAxis dataKey="nombre" type="category" width={150} stroke="#999" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #6b7280', 
                    borderRadius: '8px',
                    color: '#f9fafb'
                  }} 
                  labelStyle={{ color: '#f9fafb' }}
                  itemStyle={{ color: '#f9fafb' }}
                />
                <Bar dataKey="cantidad" name="Cantidad" fill="#8884d8">
                  {datosServiciosSolicitados.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Primera fila: Ranking de Mecánicos y Repuestos Más Usados */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Ranking de Mantenimientos por Servicios */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-md">
          <h2 className="text-lg font-medium mb-4 flex items-center">
            <span className="mr-2">🏆</span>
            Ranking de Mecánicos por Servicios
          </h2>
          
          {/* Leyenda de servicios */}
          {datosMantenimientosPorMecanico.servicios && datosMantenimientosPorMecanico.servicios.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-750 rounded-lg">
              <span className="text-xs text-gray-400 font-medium mr-2">Servicios:</span>
              {datosMantenimientosPorMecanico.servicios.map((servicio, index) => (
                <div key={servicio} className="flex items-center space-x-1">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  ></div>
                  <span className="text-xs text-gray-300">{servicio}</span>
                </div>
              ))}
            </div>
          )}
          
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {datosMantenimientosPorMecanico.mecánicos && datosMantenimientosPorMecanico.mecánicos.map((mecanico, index) => {
              const datos = datosMantenimientosPorMecanico.mecánicos;
              const maxTotal = Math.max(...datos.map(m => m.total));
              const serviciosDelMecanico = datosMantenimientosPorMecanico.servicios;
              
              // Colores para el ranking
              let colorTexto = '';
              let iconoRanking = '';
              
              if (index === 0) {
                colorTexto = 'text-yellow-300';
                iconoRanking = '🥇';
              } else if (index === 1) {
                colorTexto = 'text-gray-300';
                iconoRanking = '🥈';
              } else if (index === 2) {
                colorTexto = 'text-amber-400';
                iconoRanking = '🥉';
              } else {
                colorTexto = 'text-blue-400';
              }
              
              return (
                <div key={mecanico.mecanicoId} className="p-3 bg-gray-750 rounded-lg hover:bg-gray-700 transition-colors">
                  {/* Header con ranking y nombre */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                        {iconoRanking && <span className="text-lg">{iconoRanking}</span>}
                      </div>
                      <span className="font-medium text-white text-sm">{mecanico.nombre}</span>
                    </div>
                    <div className="text-right">
                      <span className={`font-bold text-lg ${colorTexto}`}>{mecanico.total}</span>
                      <span className="text-gray-400 text-xs ml-1">servicios</span>
                    </div>
                  </div>
                  
                  {/* Barra segmentada por servicios */}
                  <div className="relative">
                    <div className="w-full bg-gray-600 rounded-full h-4 overflow-hidden">
                      {serviciosDelMecanico.map((tipoServicio, serviceIndex) => {
                        const cantidad = mecanico.servicios[tipoServicio] || 0;
                        const porcentaje = maxTotal > 0 ? (cantidad / maxTotal) * 100 : 0;
                        
                        return cantidad > 0 ? (
                          <div
                            key={tipoServicio}
                            className="h-full float-left transition-all duration-500"
                            style={{
                              width: `${porcentaje}%`,
                              backgroundColor: COLORS[serviceIndex % COLORS.length]
                            }}
                            title={`${tipoServicio}: ${cantidad} servicios`}
                          ></div>
                        ) : null;
                      })}
                    </div>
                    
                    {/* Valores de servicios */}
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      {serviciosDelMecanico.map((tipoServicio) => {
                        const cantidad = mecanico.servicios[tipoServicio] || 0;
                        return cantidad > 0 ? (
                          <span key={tipoServicio}>{cantidad}</span>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {(!datosMantenimientosPorMecanico.mecánicos || datosMantenimientosPorMecanico.mecánicos.length === 0) && (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">📊</div>
                <p className="text-gray-400">No hay datos de mecánicos disponibles</p>
                <p className="text-gray-500 text-sm">Complete algunos mantenimientos para ver el ranking</p>
              </div>
            )}
          </div>
        </div>

        {/* Repuestos Más Usados */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-md">
          <h2 className="text-lg font-medium mb-4">Repuestos Más Usados</h2>
          
          {/* Leyenda externa */}
          <ExternalLegend 
            data={datosRepuestosMasUsados.map((item, index) => ({
              name: item.name,
              color: COLORS[index % COLORS.length]
            }))}
            title="Repuestos"
          />
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={datosRepuestosMasUsados}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {datosRepuestosMasUsados.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #6b7280', 
                    borderRadius: '8px',
                    color: '#f9fafb'
                  }} 
                  labelStyle={{ color: '#f9fafb' }}
                  itemStyle={{ color: '#f9fafb' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-gray-800 border border-gray-600 rounded-lg p-3">
                          <p className="text-white font-medium">{data.name}</p>
                          <p className="text-gray-300 text-sm">Cantidad: {data.value}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Segunda fila: Ingresos y Tiempo Promedio por Mecánico */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Ingresos por Mecánico */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-md">
          <h2 className="text-lg font-medium mb-4">Ingresos por Mecánico</h2>
          
          {/* Leyenda externa */}
          <ExternalLegend 
            data={[
              { name: 'Ingresos Generados', color: '#00C49F' }
            ]}
            title="Datos"
          />
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={datosIngresosPorMecanico}
                margin={{ top: 5, right: 30, left: 20, bottom: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis dataKey="nombre" stroke="#999" angle={-45} textAnchor="end" height={100} />
                <YAxis 
                  stroke="#999" 
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #6b7280', 
                    borderRadius: '8px',
                    color: '#f9fafb'
                  }} 
                  labelStyle={{ color: '#f9fafb' }}
                  itemStyle={{ color: '#f9fafb' }}
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Ingresos']}
                />
                <Bar dataKey="ingresos" name="Ingresos Generados" fill="#00C49F">
                  {datosIngresosPorMecanico.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tipos de Servicios por Mecánico */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-md">
          <h2 className="text-lg font-medium mb-4">Tipos de Servicios por Mecánico</h2>
          
          {/* Leyenda externa */}
          <ExternalLegend 
            data={(datosTiposServiciosPorMecanico.servicios || []).map((servicio, index) => ({
              name: servicio,
              color: COLORS[index % COLORS.length]
            }))}
            title="Servicios"
          />
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={datosTiposServiciosPorMecanico.data}
                margin={{ top: 5, right: 30, left: 20, bottom: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis dataKey="nombre" stroke="#999" angle={-45} textAnchor="end" height={100} />
                <YAxis stroke="#999" allowDecimals={false} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #6b7280', 
                    borderRadius: '8px',
                    color: '#f9fafb'
                  }} 
                  labelStyle={{ color: '#f9fafb' }}
                  itemStyle={{ color: '#f9fafb' }}
                />
                {(datosTiposServiciosPorMecanico.servicios || []).map((servicio, index) => (
                  <Bar 
                    key={servicio}
                    dataKey={servicio} 
                    name={servicio}
                    fill={COLORS[index % COLORS.length]}
                    stackId="servicios"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Repuestos con bajo stock - Sección completa */}
      {stockBajo.length > 0 && (
        <div className="mt-6">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-md">
            <h2 className="text-lg font-medium mb-4">Repuestos con Bajo Stock</h2>
            <div className="text-xs text-gray-400 mb-4">
              Mostrando repuestos de: {sucursalData ? sucursalData.Nombre : 'todas las sucursales'}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Repuesto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Stock Actual
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Stock Mínimo
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {stockBajo.map((repuesto) => (
                    <tr key={repuesto.id} className="hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">{repuesto.Nombre}</div>
                        {repuesto.Descripcion && (
                          <div className="text-sm text-gray-400">{repuesto.Descripcion}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-300">{repuesto.Codigo}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          repuesto.Stock === 0 
                            ? 'bg-red-900 text-red-100' 
                            : 'bg-yellow-900 text-yellow-100'
                        }`}>
                          {repuesto.Stock} unidades
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-300">{repuesto.StockMinimo} unidades</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de prueba expirada */}
      <TrialExpiredModal 
        isOpen={showTrialModal} 
        onClose={() => setShowTrialModal(false)} 
      />

    </div>
  );
};

export default Dashboard;
